import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner, Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  fetchSubscriptionPlans,
  fetchSuperAdminOrganizations,
  assignPlanToOrg,
  createPlan,
  updatePlan
} from '../../../actioncreators/actioncreators';
import './SuperAdmin.css';

// Icons
const PackageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-5 3c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm4 8h-8v-1c0-1.33 2.67-2 4-2s4 .67 4 2v1z"/>
  </svg>
);

const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

const CrossIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

const UsersIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
  </svg>
);

const AssignIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
  </svg>
);

const AddIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
  </svg>
);

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [startDate, setStartDate] = useState({ day: '', month: '', year: '' });
  const [endDate, setEndDate] = useState({ day: '', month: '', year: '' });

  const assignMonths = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const assignDays = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
  const assignYears = Array.from({ length: 201 }, (_, i) => String(1900 + i));
  const buildAssignDate = ({ day, month, year }) => (day && month && year) ? `${year}-${month}-${day}` : '';
  const handleAssignDatePart = (which, part, value) => {
    const setter = which === 'start' ? setStartDate : setEndDate;
    setter(prev => ({ ...prev, [part]: value }));
    setAssignDateError('');
  };
  const [maxUsers, setMaxUsers] = useState('10');
  const [actionLoading, setActionLoading] = useState(false);
  const [currentOrgPlan, setCurrentOrgPlan] = useState('');
  const [assignDateError, setAssignDateError] = useState('');

  // Add Plan modal states
  const [showAddPlanModal, setShowAddPlanModal] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanType, setNewPlanType] = useState('');
  const [newPlanPrice, setNewPlanPrice] = useState('');
  const [newPlanMinUsers, setNewPlanMinUsers] = useState('1');
  const [newPlanFaceId, setNewPlanFaceId] = useState(false);
  const [newPlanReports, setNewPlanReports] = useState(false);
  const [newPlanStatus, setNewPlanStatus] = useState('ACTIVE');

  // Edit Plan modal states
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  // Derive unique plan types from fetched plans, always including base types
  const BASE_PLAN_TYPES = ['Trial', 'Basic', 'Advance'];
  const planTypeOptions = [
    ...BASE_PLAN_TYPES,
    ...plans
      .map(p => p.package_type)
      .filter(t => t && !BASE_PLAN_TYPES.includes(t)),
  ].filter((v, i, arr) => arr.indexOf(v) === i);

  useEffect(() => {
    fetchAllPlans();
    fetchOrgs();
  }, []);

  const fetchAllPlans = async () => {
    try {
      const response = await fetchSubscriptionPlans();
      setPlans(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgs = async () => {
    try {
      const response = await fetchSuperAdminOrganizations({ limit: 100 });
      setOrganizations(response.data?.data?.organizations || []);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const handleOrgChange = (orgId) => {
    setSelectedOrg(orgId);
    const org = organizations.find(o => o.org_id === orgId);
    setCurrentOrgPlan(org?.plan || org?.package_type || 'N/A');
  };

  const handleAssignPlan = async () => {
    if (!selectedOrg || !selectedPlan) return;

    const startStr = buildAssignDate(startDate);
    const endStr = buildAssignDate(endDate);

    if (!startStr) {
      setAssignDateError('Start date is required.');
      return;
    }
    if (!endStr) {
      setAssignDateError('End date is required.');
      return;
    }
    if (endStr < startStr) {
      setAssignDateError('End date must be greater than or equal to start date.');
      return;
    }
    setAssignDateError('');

    setActionLoading(true);
    try {
      const startDateTime = new Date(startStr + 'T00:00:00').toISOString();
      const endDateTime = new Date(endStr + 'T23:59:59').toISOString();

      await assignPlanToOrg({
        org_id: selectedOrg,
        package_id: selectedPlan,
        max_users: parseInt(maxUsers, 10) || 1,
        start_date: startDateTime,
        end_date: endDateTime
      });
      setShowAssignModal(false);
      resetAssignForm();
      fetchOrgs();
    } catch (error) {
      console.error('Failed to assign plan:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const resetAssignForm = () => {
    setSelectedOrg('');
    setSelectedPlan('');
    setStartDate({ day: '', month: '', year: '' });
    setEndDate({ day: '', month: '', year: '' });
    setMaxUsers('10');
    setCurrentOrgPlan('');
    setAssignDateError('');
  };

  const openAssignModal = () => {
    resetAssignForm();
    setShowAssignModal(true);
  };

  const openAddPlanModal = () => {
    resetAddPlanForm();
    setShowAddPlanModal(true);
  };

  const resetAddPlanForm = () => {
    setNewPlanName('');
    setNewPlanType('');
    setNewPlanPrice('');
    setNewPlanMinUsers('1');
    setNewPlanFaceId(false);
    setNewPlanReports(false);
    setNewPlanStatus('ACTIVE');
  };

  // Returns { price, minUsers } if valid, or null after showing a toast.
  const validatePlanForm = () => {
    const trimmedName = newPlanName.trim();
    if (trimmedName.length < 2 || trimmedName.length > 100) {
      toast.warning('Plan name must be between 2 and 100 characters.');
      return null;
    }
    if (!newPlanType) {
      toast.warning('Please select a plan type.');
      return null;
    }
    const priceStr = String(newPlanPrice).trim();
    const price = priceStr === '' ? 0 : parseFloat(priceStr);
    if (isNaN(price) || price < 0 || price > 999999.99) {
      toast.warning('Monthly price must be a number between 0 and 999,999.99.');
      return null;
    }
    const minUsersStr = String(newPlanMinUsers).trim();
    const minUsers = minUsersStr === '' ? 1 : parseInt(minUsersStr, 10);
    if (isNaN(minUsers) || minUsers < 1 || minUsers > 100000) {
      toast.warning('Minimum users must be a whole number between 1 and 100,000.');
      return null;
    }
    return { price, minUsers };
  };

  const handleAddPlan = async () => {
    const validated = validatePlanForm();
    if (!validated) return;
    setActionLoading(true);
    try {
      await createPlan({
        package_name: newPlanName.trim(),
        package_type: newPlanType,
        monthly_price_per_user: validated.price,
        min_users: validated.minUsers,
        has_face_id: newPlanFaceId,
        has_advanced_reports: newPlanReports,
        status: newPlanStatus,
      });

      await fetchAllPlans();
      setShowAddPlanModal(false);
      resetAddPlanForm();
      toast.success('Plan created successfully!');
    } catch (error) {
      console.error('Failed to create plan:', error);
      toast.error('Failed to create plan: ' + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const openEditPlanModal = (plan) => {
    setEditingPlan(plan);
    setNewPlanName(plan.package_name);
    setNewPlanType(plan.package_type);
    setNewPlanPrice(plan.monthly_price_per_user != null ? String(plan.monthly_price_per_user) : '');
    setNewPlanMinUsers(plan.min_users != null ? String(plan.min_users) : '1');
    setNewPlanFaceId(!!plan.has_face_id);
    setNewPlanReports(!!plan.has_advanced_reports);
    setNewPlanStatus(plan.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE');
    setShowEditPlanModal(true);
  };

  const handleEditPlan = async () => {
    if (!editingPlan) return;
    const validated = validatePlanForm();
    if (!validated) return;
    setActionLoading(true);
    try {
      await updatePlan(editingPlan.package_id, {
        package_name: newPlanName.trim(),
        package_type: newPlanType,
        monthly_price_per_user: validated.price,
        min_users: validated.minUsers,
        has_face_id: newPlanFaceId,
        has_advanced_reports: newPlanReports,
        status: newPlanStatus,
      });

      await fetchAllPlans();
      setShowEditPlanModal(false);
      setEditingPlan(null);
      resetAddPlanForm();
      toast.success('Plan updated successfully!');
    } catch (error) {
      console.error('Failed to update plan:', error);
      toast.error('Failed to update plan: ' + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'Free';
    return `₹${price}`;
  };

  const getPlanTypePill = (type) => {
    const typeClass = (type || 'default').toLowerCase();
    return <span className={`plan-type-pill plan-type-${typeClass}`}>{type || 'N/A'}</span>;
  };

  if (loading) {
    return (
      <div className="super-admin-page">
        <div className="loading-container" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" />
          <span className="loading-text">Loading subscription plans...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="super-admin-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-icon">
            <PackageIcon />
          </div>
          <div>
            <h1 className="page-title">Subscription Plans</h1>
            <p className="page-subtitle">{plans.length} available plans</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button className="btn-create" onClick={openAddPlanModal}>
            <AddIcon />
            <span>Add Plan</span>
          </Button>
          <Button className="btn-create" onClick={openAssignModal}>
            <AssignIcon />
            <span>Assign Plan</span>
          </Button>
        </div>
      </div>

      {/* Plans Table */}
      <Card className="table-card">
        {plans.length === 0 ? (
          <div className="empty-state">
            <PackageIcon />
            <p>No subscription plans found</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table plans-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Price</th>
                  <th>Users</th>
                  <th className="th-center">Face ID</th>
                  <th className="th-center">Reports</th>
                  <th className="th-center">Status</th>
                  <th className="th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.package_id}>
                    <td>
                      <div className="plan-cell">
                        <div className="plan-info">
                          <span className="plan-name">{plan.package_name}</span>
                          {getPlanTypePill(plan.package_type)}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="price-cell">
                        <span className="price-amount">{formatPrice(plan.monthly_price_per_user)}</span>
                        {plan.monthly_price_per_user > 0 && (
                          <span className="price-period">/user/month</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="users-limit-cell">
                        <UsersIcon />
                        <span>{plan.min_users || 'Unlimited'}</span>
                      </div>
                    </td>
                    <td className="td-center">
                      <span className={`feature-indicator ${plan.has_face_id ? 'feature-yes' : 'feature-no'}`}>
                        {plan.has_face_id ? <CheckIcon /> : <CrossIcon />}
                      </span>
                    </td>
                    <td className="td-center">
                      <span className={`feature-indicator ${plan.has_advanced_reports ? 'feature-yes' : 'feature-no'}`}>
                        {plan.has_advanced_reports ? <CheckIcon /> : <CrossIcon />}
                      </span>
                    </td>
                    <td className="td-center">
                      <span className={`status-pill ${plan.status === 'ACTIVE' ? 'status-active' : 'status-inactive'}`}>
                        {plan.status || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn-icon btn-edit"
                          title="Edit Plan"
                          onClick={() => openEditPlanModal(plan)}
                        >
                          <EditIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add Plan Modal */}
      <Modal
        show={showAddPlanModal}
        onHide={() => setShowAddPlanModal(false)}
        centered
        className="custom-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="modal-title-custom">
            <AddIcon style={{ marginRight: '8px' }} />
            Add New Plan
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Plan Name *</Form.Label>
            <Form.Control
              type="text"
              className="form-input"
              placeholder="e.g., Enterprise Plan"
              value={newPlanName}
              onChange={(e) => setNewPlanName(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Plan Type *</Form.Label>
            <Form.Select
              className="filter-select"
              value={newPlanType}
              onChange={(e) => setNewPlanType(e.target.value)}
            >
              <option value="">Select plan type...</option>
              {planTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Monthly Price per User (₹)</Form.Label>
            <Form.Control
              type="number"
              className="form-input"
              value={newPlanPrice}
              onChange={(e) => {
                const raw = e.target.value;
                // Strip leading zeros (e.g. "05" → "5") but keep a lone "0" and "0." for decimals
                const stripped = raw.replace(/^0+(?=\d)/, '');
                setNewPlanPrice(stripped);
              }}
              onFocus={(e) => e.target.select()}
              onBlur={() => {
                const v = parseFloat(String(newPlanPrice).trim());
                if (!isNaN(v)) setNewPlanPrice(String(v));
              }}
              min={0}
              max={999999.99}
              step={0.01}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Minimum Users</Form.Label>
            <Form.Control
              type="number"
              className="form-input"
              value={newPlanMinUsers}
              onChange={(e) => {
                const raw = e.target.value;
                setNewPlanMinUsers(raw.replace(/^0+(?=\d)/, ''));
              }}
              onFocus={(e) => e.target.select()}
              onBlur={() => {
                const v = parseInt(String(newPlanMinUsers).trim(), 10);
                setNewPlanMinUsers(isNaN(v) || v < 1 ? '1' : String(v));
              }}
              min={1}
              max={100000}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Features</Form.Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="add-plan-face-id"
                  type="checkbox"
                  checked={newPlanFaceId}
                  onChange={(e) => setNewPlanFaceId(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: '#FF8800', cursor: 'pointer', flexShrink: 0 }}
                />
                <label htmlFor="add-plan-face-id" style={{ margin: 0, cursor: 'pointer', fontWeight: 500 }}>Face ID Recognition</label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="add-plan-reports"
                  type="checkbox"
                  checked={newPlanReports}
                  onChange={(e) => setNewPlanReports(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: '#FF8800', cursor: 'pointer', flexShrink: 0 }}
                />
                <label htmlFor="add-plan-reports" style={{ margin: 0, cursor: 'pointer', fontWeight: 500 }}>Advanced Reports</label>
              </div>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Status</Form.Label>
            <Form.Select
              className="filter-select"
              value={newPlanStatus}
              onChange={(e) => setNewPlanStatus(e.target.value)}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          <Button className="btn-cancel" onClick={() => setShowAddPlanModal(false)}>
            Cancel
          </Button>
          <Button
            className="btn-primary-custom"
            onClick={handleAddPlan}
            disabled={!newPlanName || !newPlanType || actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : 'Create Plan'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Plan Modal */}
      <Modal
        show={showEditPlanModal}
        onHide={() => {
          setShowEditPlanModal(false);
          setEditingPlan(null);
          resetAddPlanForm();
        }}
        centered
        className="custom-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="modal-title-custom">
            <EditIcon style={{ marginRight: '8px' }} />
            Edit Plan
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Plan Name *</Form.Label>
            <Form.Control
              type="text"
              className="form-input"
              placeholder="e.g., Enterprise Plan"
              value={newPlanName}
              onChange={(e) => setNewPlanName(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Plan Type *</Form.Label>
            <Form.Select
              className="filter-select"
              value={newPlanType}
              onChange={(e) => setNewPlanType(e.target.value)}
            >
              <option value="">Select plan type...</option>
              {planTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Monthly Price per User (₹)</Form.Label>
            <Form.Control
              type="number"
              className="form-input"
              value={newPlanPrice}
              onChange={(e) => {
                const raw = e.target.value;
                // Strip leading zeros (e.g. "05" → "5") but keep a lone "0" and "0." for decimals
                const stripped = raw.replace(/^0+(?=\d)/, '');
                setNewPlanPrice(stripped);
              }}
              onFocus={(e) => e.target.select()}
              onBlur={() => {
                const v = parseFloat(String(newPlanPrice).trim());
                if (!isNaN(v)) setNewPlanPrice(String(v));
              }}
              min={0}
              max={999999.99}
              step={0.01}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Minimum Users</Form.Label>
            <Form.Control
              type="number"
              className="form-input"
              value={newPlanMinUsers}
              onChange={(e) => {
                const raw = e.target.value;
                setNewPlanMinUsers(raw.replace(/^0+(?=\d)/, ''));
              }}
              onFocus={(e) => e.target.select()}
              onBlur={() => {
                const v = parseInt(String(newPlanMinUsers).trim(), 10);
                setNewPlanMinUsers(isNaN(v) || v < 1 ? '1' : String(v));
              }}
              min={1}
              max={100000}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Features</Form.Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="edit-plan-face-id"
                  type="checkbox"
                  checked={newPlanFaceId}
                  onChange={(e) => setNewPlanFaceId(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: '#FF8800', cursor: 'pointer', flexShrink: 0 }}
                />
                <label htmlFor="edit-plan-face-id" style={{ margin: 0, cursor: 'pointer', fontWeight: 500 }}>Face ID Recognition</label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="edit-plan-reports"
                  type="checkbox"
                  checked={newPlanReports}
                  onChange={(e) => setNewPlanReports(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: '#FF8800', cursor: 'pointer', flexShrink: 0 }}
                />
                <label htmlFor="edit-plan-reports" style={{ margin: 0, cursor: 'pointer', fontWeight: 500 }}>Advanced Reports</label>
              </div>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Status</Form.Label>
            <Form.Select
              className="filter-select"
              value={newPlanStatus}
              onChange={(e) => setNewPlanStatus(e.target.value)}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          <Button className="btn-cancel" onClick={() => {
            setShowEditPlanModal(false);
            setEditingPlan(null);
            resetAddPlanForm();
          }}>
            Cancel
          </Button>
          <Button
            className="btn-primary-custom"
            onClick={handleEditPlan}
            disabled={!newPlanName || !newPlanType || actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : 'Update Plan'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assign Plan Modal */}
      <Modal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        centered
        className="custom-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="modal-title-custom">
            <AssignIcon style={{ marginRight: '8px' }} />
            Assign Plan to Organization
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">Organization</Form.Label>
            <Form.Select
              className="filter-select"
              value={selectedOrg}
              onChange={(e) => handleOrgChange(e.target.value)}
            >
              <option value="">Select an organization...</option>
              {organizations.map((org) => (
                <option key={org.org_id} value={org.org_id}>
                  {org.org_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {selectedOrg && (
            <div className="current-plan-info mb-3">
              <span className="current-plan-label">Current Plan:</span>
              <span className={`current-plan-value plan-text plan-${currentOrgPlan.toLowerCase()}`}>
                {currentOrgPlan}
              </span>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="form-label-custom">New Plan</Form.Label>
            <Form.Select
              className="filter-select"
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
            >
              <option value="">Select a plan...</option>
              {plans.filter(p => p.status === 'ACTIVE').map((plan) => (
                <option key={plan.package_id} value={plan.package_id}>
                  {plan.package_name} - {plan.package_type}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <div className="date-row">
            <Form.Group className="date-field">
              <Form.Label className="form-label-custom">Start Date *</Form.Label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <Form.Select style={{ flex: 1 }} className="filter-select" value={startDate.day} onChange={e => handleAssignDatePart('start', 'day', e.target.value)}>
                  <option value="">Day</option>
                  {assignDays.map(d => <option key={d} value={d}>{d}</option>)}
                </Form.Select>
                <Form.Select style={{ flex: 2 }} className="filter-select" value={startDate.month} onChange={e => handleAssignDatePart('start', 'month', e.target.value)}>
                  <option value="">Month</option>
                  {assignMonths.map((m, i) => <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>)}
                </Form.Select>
                <Form.Select style={{ flex: 2 }} className="filter-select" value={startDate.year} onChange={e => handleAssignDatePart('start', 'year', e.target.value)}>
                  <option value="">Year</option>
                  {assignYears.map(y => <option key={y} value={y}>{y}</option>)}
                </Form.Select>
              </div>
            </Form.Group>
            <Form.Group className="date-field">
              <Form.Label className="form-label-custom">End Date *</Form.Label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <Form.Select style={{ flex: 1 }} className="filter-select" value={endDate.day} onChange={e => handleAssignDatePart('end', 'day', e.target.value)}>
                  <option value="">Day</option>
                  {assignDays.map(d => <option key={d} value={d}>{d}</option>)}
                </Form.Select>
                <Form.Select style={{ flex: 2 }} className="filter-select" value={endDate.month} onChange={e => handleAssignDatePart('end', 'month', e.target.value)}>
                  <option value="">Month</option>
                  {assignMonths.map((m, i) => <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>)}
                </Form.Select>
                <Form.Select style={{ flex: 2 }} className="filter-select" value={endDate.year} onChange={e => handleAssignDatePart('end', 'year', e.target.value)}>
                  <option value="">Year</option>
                  {assignYears.map(y => <option key={y} value={y}>{y}</option>)}
                </Form.Select>
              </div>
              {assignDateError && (
                <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                  {assignDateError}
                </div>
              )}
            </Form.Group>
          </div>

          <Form.Group className="mt-3">
            <Form.Label className="form-label-custom">Max Users</Form.Label>
            <Form.Control
              type="number"
              className="form-input"
              value={maxUsers}
              onChange={(e) => setMaxUsers(e.target.value.replace(/^0+(?=\d)/, ''))}
              onFocus={(e) => e.target.select()}
              onBlur={() => {
                const v = parseInt(maxUsers, 10);
                setMaxUsers(isNaN(v) || v < 1 ? '1' : String(v));
              }}
              min={1}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          <Button className="btn-cancel" onClick={() => setShowAssignModal(false)}>
            Cancel
          </Button>
          <Button
            className="btn-primary-custom"
            onClick={handleAssignPlan}
            disabled={!selectedOrg || !selectedPlan || actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SubscriptionPlans;
